import { changeHostToHK } from '@didi/webx-js-core';
import { utils } from '../utils';
import { isXpub } from '../utils/isXpub';
import { query, hashQuery } from '../query';
import { getOmega } from '../omega';
import { getNineChapterCommonParams } from '../statistic';
import { isHK } from '../xenv';
/**
 * @description: 获取上报的链接
 * @param {string} url
 * @return {string}
 */
function getPath(url) {
    const urlObj = url ? new URL(url) : {};
    return urlObj.hostname + urlObj.pathname;
}
/**
 * @description: 获取网络请求列表
 * @return {PerformanceEntry[]}
 */
function getRequestList() {
    // 过滤掉omega埋点上报的请求不然太多了
    const resourceList = performance.getEntriesByType('resource').filter((resource) => resource.initiatorType === 'xmlhttprequest' && resource.name.indexOf('omgup') === -1);
    return resourceList;
}
/**
 * @description: 请求上报埋点方法封装
 * @return {void}
 */
function requestTrack(params) {
    try {
        const normalizeHeaders = {};
        // 将headers的key都转为小写
        Object.entries(params.headers).map(([key, value]) => {
            normalizeHeaders[key.toLowerCase()] = value;
        });
        // 列举trace的可能key值
        const possibleTraceKeys = ['traceId', 'traceid', 'trace_id', 'x-trace-id', 'x-request-id'];
        // 找到第一个有值的trace
        const traceId = possibleTraceKeys.reduce((resTrace, curKey) => {
            return resTrace || params.data?.[curKey] || normalizeHeaders[curKey];
        }, null);
        const api = getPath(params.requestUrl);
        const Query = Object.assign({}, query(), hashQuery());
        getOmega(omega => {
            omega.trackEvent?.('pub_webx_request_bt', 'webx_request上报', {
                type: params.type,
                api,
                prod_key: Query.prod_key,
                dchn: Query.dchn,
                trace_id: traceId,
                errmsg: params.errMsg
            });
        });
        if (params.type === 'error')
            return;
        // 性能上报
        const requestList = getRequestList();
        const perfTiming = requestList.filter(request => {
            const urlObj = new URL(request.name);
            let checkPath = urlObj.pathname;
            if (urlObj.hostname.indexOf('comps.intra.xiaojukeji.com') !== -1) {
                checkPath = urlObj.pathname.substring(7);
            }
            if (checkPath === new URL(params.requestUrl).pathname) {
                return request;
            }
        });
        window.PerfTrack.customTrack('$_request_perf', {
            perfTiming: perfTiming?.[0] || {}
        });
        // eslint-disable-next-line no-empty
    }
    catch (_) { }
}
class WebxRequestMpx {
    constructor(options = {}, requestCreateConfig = { useSingletonConfig: true }) {
        this.mpx = window.__mpx;
        this.mpxFetch = window.$__mpxFetch;
        this.mpx.use(this.mpxFetch);
        this._requestCreateConfig = requestCreateConfig;
        this._request = this._getRequestInstance(options);
        this._addInterceptors();
        this._addSingletonConfig();
        this._proxyAndTrack();
    }
    _proxyAndTrack() {
        this._request.interceptors?.response?.use(response => {
            requestTrack({
                requestUrl: response.requestConfig.url,
                data: response.data,
                errMsg: response.errMsg,
                headers: response.header,
                type: 'success'
            });
            return response;
        });
    }
    _getRequestInstance(options) {
        const mpx = this.mpx;
        return this._requestCreateConfig.useSingleton ? mpx.xfetch : mpx.xfetch.create(Object.assign({}, options, { useQueue: mpx.xfetch.useQueue }));
    }
    _addInterceptors() {
        if (!this._requestCreateConfig.useSingleton) {
            this.interceptors = this._request.interceptors;
        }
    }
    _addSingletonConfig() {
        const mpx = this.mpx;
        if (!this._requestCreateConfig.useSingletonConfig)
            return;
        // 添加过滤器
        if (mpx.xfetch.interceptors) {
            for (let key in mpx.xfetch.interceptors) {
                if (this.interceptors[key]) {
                    const interceptors = mpx.xfetch.interceptors[key].interceptors;
                    interceptors && interceptors.forEach(interceptor => {
                        this.interceptors[key].use(interceptor.fulfilled, interceptor.reject);
                    });
                }
            }
        }
        // 添加代理(控制中心)
        this._request.setProxy(mpx.xfetch.getProxy());
        // 添加签名白名单
        this._request.addSignWhiteList(mpx.xfetch.signWhiteList);
    }
}
class WebxRequest {
    constructor(options = {}, requestCreateConfig = {}) {
        this._requestCreateConfig = requestCreateConfig;
        this._request = this._getRequestInstance(options);
        this._addInterceptors();
        this._proxyAndTrack();
    }
    _proxyAndTrack() {
        this._request.interceptors?.response?.use(response => {
            requestTrack({
                requestUrl: response.config?.url,
                data: response.data,
                errMsg: response.statusText,
                headers: response.headers,
                type: 'success'
            });
            return response;
        });
    }
    _getRequestInstance(options) {
        const Dajax = window.axios.default;
        return this._requestCreateConfig.useSingleton ? Dajax : Dajax.create(options);
    }
    _addInterceptors() {
        if (!this._requestCreateConfig.useSingleton) {
            this.interceptors = this._request.interceptors;
        }
    }
}
const createInstanceMpx = (...args) => {
    const instance = new WebxRequestMpx(...args);
    const request = (method, url, { params = {}, data = {}, header = {}, isWebx = false, emulateJSON = false, giveUpNineChapter = false, needSign = false, timeout = 0, cancelToken = false } = {}) => {
        return new Promise((resolve, reject) => {
            /* xpub内请求不会发出，同时会一直pending */
            if (isXpub()) {
                console.log(`request blocked in xpub: ${url}`);
                return;
            }
            /* post/get预览逻辑处理 */
            utils.handleMockviewTag(header, data['dchn'] || params['dchn'] || null);
            /* End 预览逻辑 */
            /* 注入九章参数 */
            const nineChapterParams = getNineChapterCommonParams(); // 获取九章参数
            delete nineChapterParams['uid'];
            if (isWebx)
                delete nineChapterParams['xspm'];
            let _data = data;
            let _params = params;
            if (method.toLowerCase() === 'post') {
                /* post请求 则注入data */
                if (!giveUpNineChapter) {
                    _data = Object.assign({}, nineChapterParams, data);
                    if (/webx\/chapter\/cover\/config/g.test(url)) {
                        /* cover/config接口特殊规则 xspm_from&xenv&dchn由传参决定 */
                        _data['xspm_from'] = data['xspm_from'] || nineChapterParams['xspm_from'];
                        _data['dchn'] = data['dchn'];
                        _data['xenv'] = data['xenv'];
                    }
                }
            }
            else if (method.toLowerCase() === 'get') {
                /* get请求 则注入params */
                if (!giveUpNineChapter) {
                    _params = Object.assign({}, nineChapterParams, params);
                    if (/webx\/chapter\/cover\/config/g.test(url)) {
                        /* cover/config接口特殊规则 xspm_from&xenv&dchn由传参决定 */
                        _params['xspm_from'] = params['xspm_from'] || nineChapterParams['xspm_from'];
                        _params['dchn'] = params['dchn'];
                        _params['xenv'] = params['xenv'];
                    }
                }
            }
            /* End 注入九章参数 */
            /* 香港地区统一更换host */
            if (isHK)
                url = changeHostToHK(url);
            /* End 香港地区统一更换host */
            instance._request.fetch({
                url: url,
                method,
                data: _data,
                params: _params,
                header,
                emulateJSON,
                needSign,
                timeout,
                cancelToken
            }).then(res => {
                if (!res || +res.statusCode !== 200) {
                    requestTrack({
                        requestUrl: url,
                        data: res?.data,
                        errMsg: res?.errMsg,
                        headers: res?.header,
                        type: 'error'
                    });
                    reject(res);
                    return;
                }
                const { data } = res;
                resolve(data);
            }).catch(err => {
                requestTrack({
                    requestUrl: url,
                    data: err?.data,
                    errMsg: err?.errMsg,
                    headers: err?.header,
                    type: 'error'
                });
                reject(err);
            });
        });
    };
    request.interceptors = instance.interceptors; // 每一个request实例都有interceptors
    return request;
};
const createInstance = (...args) => {
    const instance = new WebxRequest(...args);
    const request = (method, url, { params = {}, data = {}, header = {}, emulateJSON = false, giveUpNineChapter = false, needSign = false, timeout = 0, cancelToken = false, noDomainCheck = false } = {}) => {
        /* xpub内请求不会发出，同时会一直pending */
        if (isXpub()) {
            return new Promise(() => {
                console.log(`request blocked in xpub: ${url}`);
            });
        }
        const _method = method.toLowerCase();
        /* post/get预览逻辑处理 */
        utils.handleMockviewTag(header, data['dchn'] || params['dchn'] || null);
        /* End 预览逻辑 */
        /* 注入九章参数 */
        const nineChapterParams = getNineChapterCommonParams(); // 获取九章参数
        delete nineChapterParams['uid'];
        let _data = data;
        let _params = params;
        if (_method === 'post') {
            /* post请求 则注入data */
            if (!giveUpNineChapter) {
                _data = Object.assign({}, nineChapterParams, data);
                if (/webx\/chapter\/cover\/config/g.test(url)) {
                    /* cover/config接口特殊规则 xenv&dchn由传参决定 */
                    _data['uid'] = '';
                    _data['xspm_from'] = data['xspm_from'] || nineChapterParams['xspm_from'];
                    _data['dchn'] = data['dchn'];
                    _data['xenv'] = data['xenv'];
                }
            }
        }
        else if (_method === 'get') {
            /* get请求 则注入params */
            if (!giveUpNineChapter) {
                _params = Object.assign({}, nineChapterParams, params);
                if (/webx\/chapter\/cover\/config/g.test(url)) {
                    /* cover/config接口特殊规则 xenv&dchn由传参决定 */
                    _params['uid'] = '';
                    _params['xspm_from'] = params['xspm_from'] || nineChapterParams['xspm_from'];
                    _params['dchn'] = params['dchn'];
                    _params['xenv'] = params['xenv'];
                }
            }
        }
        /* End 注入九章参数 */
        // 设置默认请求参数
        const config = {
            url,
            method: _method,
            params: _params,
            headers: header,
            needEncrypt: needSign,
            emulateJSON,
            timeout,
            cancelToken,
            noDomainCheck
        };
        if (Object.keys(data).length) {
            config.data = _data;
        }
        return instance._request.request(config).then((res) => res.json()).catch(err => {
            requestTrack({
                type: 'error',
                errMsg: err,
                requestUrl: url
            });
            return Promise.reject(err);
        });
    };
    request.interceptors = instance.interceptors; // 每一个request实例都有interceptors
    return request;
};
let instance;
const request = (...args) => {
    if (instance) {
        return instance(...args);
    }
    if (window.__mpx && window.$__mpxFetch) {
        console.log('=== 正在使用环境变量mpx-fetch进行网络请求 ===');
        const mpx = window.__mpx;
        instance = createInstanceMpx({}, { useSingleton: true });
        request.interceptors = instance.interceptors;
        Object.defineProperty(instance, 'cancelToken', {
            get() {
                return new mpx.xfetch.CancelToken();
            }
        });
    }
    else if (window.axios?.default) {
        const Dajax = window.axios.default;
        console.log('=== 正在使用dajax进行网络请求 ===');
        instance = createInstance({}, { useSingleton: true });
        request.interceptors = instance.interceptors;
        Object.defineProperty(instance, 'cancelToken', {
            get() {
                return Dajax.CancelToken.source();
            }
        });
        Object.defineProperty(instance, 'libType', {
            get() {
                return 'dajax';
            }
        });
    }
    else {
        if (!isXpub()) {
            console.error('请在 HTML head 中加入 <script src="https://ut-static.udache.com/webx/web_static/libs/dajax/2.1.24/dajax.min.js"></script>');
            throw new Error('既没有引用 MPX Fetch, 也没有引用 Dajax. 无法发送网络请求。');
        }
    }
    return instance(...args);
};
request.interceptors = undefined;
request.create = function (...args) {
    if (window.__mpx && window.$__mpxFetch) {
        return createInstanceMpx(...args);
    }
    else if (window.axios?.default) {
        return createInstance(...args);
    }
    else {
        if (!isXpub()) {
            console.error('请在 HTML head 中加入 <script src="https://ut-static.udache.com/webx/web_static/libs/dajax/2.1.24/dajax.min.js"></script>');
            throw new Error('既没有引用 MPX Fetch, 也没有引用 Dajax. 无法发送网络请求。');
        }
    }
};
const get = (url, config = {}) => {
    return request('GET', url, config);
};
const post = (url, data = {}, config = {}) => {
    config.data = data;
    return request('POST', url, config);
};
const postEncrypt = async (url, data = {}, config = {}) => {
    const nineChapterParams = getNineChapterCommonParams(); // 获取九章参数
    const _data = Object.assign({}, nineChapterParams, data);
    const encryptData = await utils.setEncrypt(_data);
    config.header = {
        ...config.header,
        'Encrypt-Type': 'aes',
        'Encrypt-Version': 1,
    };
    /* postEncrypt预览逻辑处理 */
    utils.handleMockviewTag(config.header, _data['dchn'] || null);
    /* End 预览逻辑 */
    config.giveUpNineChapter = true;
    return post(url, { encrypt_data: encryptData }, config);
};
export { request, get, post, postEncrypt, };
